import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import {
  supabase,
  persistSessionToBackend,
  clearBackendSession,
} from "../services/supabase";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "../config/env";
import { setUserContext, clearUserContext } from "../config/sentry";
import { analytics } from "../config/analytics";
import { clearUser } from "../services/monitoring";
import { initLogger, logger } from "../utils/logger";
import { getCsrfToken } from "../services/csrf";
import apiClient from "../services/apiClient";
import { queryClient } from "../services/queryClient";
import { AnimatePresence } from "framer-motion";
import { MFAChallenge } from "../components/security/MFAChallenge";

interface BetaUser {
  id: string;
  email?: string;
  name?: string;
  createdAt?: string;
}

interface BetaStatus {
  hasAccess: boolean;
  type?: string;
  betaUser?: BetaUser;
  invitationCode?: string;
  status?: string;
}

interface SignUpMetadata {
  firstName?: string;
  lastName?: string;
  betaCode?: string;
  guestToken?: string;
}

interface SignUpResult {
  requiresEmailConfirmation?: boolean;
  user?: User;
  session?: Session;
  accessToken?: string;
  refreshToken?: string;
}

interface SignInResult {
  requiresMFA?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  betaStatus: BetaStatus | null;
  /** Always 'supabase' after legacy JWT removal */
  authType: 'supabase';
  signIn: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<SignInResult>;
  signUp: (
    email: string,
    password: string,
    metadata?: SignUpMetadata,
  ) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
  /** @deprecated Use signOut */
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  checkBetaAccess: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Public routes that don't require authentication

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [betaStatus, setBetaStatus] = useState<BetaStatus | null>(null);
  const [pendingMFAVerification, setPendingMFAVerification] = useState(false);
  const [pendingNavigationTarget, setPendingNavigationTarget] = useState<
    string | null
  >(null);
  // Store pending session/user until MFA is verified (not exposed to app)
  const [pendingSession, setPendingSession] = useState<Session | null>(null);
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  const [pendingRememberMe, setPendingRememberMe] = useState(false);
  // Ref to track MFA pending state for auth listener (state can't be accessed in listener closure)
  const mfaPendingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize authentication state
  const initializeAuth = useCallback(async () => {
    try {
      logger.debug("[Auth] Initializing Supabase authentication...");

      // Get the current Supabase session
      const {
        data: { session: supabaseSession },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        logger.error("[Auth] Error getting session:", sessionError);
        setError(sessionError.message);
      } else if (supabaseSession) {
        logger.debug("[Auth] Found existing Supabase session");
        setSession(supabaseSession);
        setUser(supabaseSession.user);
        // Initialize logger with user email for admin logging
        initLogger(supabaseSession.user.email || null);
        // CRITICAL: Await session persistence so backend auth cookies are set
        // before ProtectedRoute renders children that make authenticated API calls.
        // Without this, API calls may fire before cookies are set → 401 → redirect loop.
        await persistSessionToBackend(supabaseSession);
      } else {
        logger.debug("[Auth] No existing session found");
        // Initialize logger without user (non-admin mode)
        initLogger(null);
      }
    } catch (err) {
      logger.error("[Auth] Initialization error:", err);
      setError("Failed to initialize authentication");
    } finally {
      setLoading(false);
    }
  }, []);

  // Set up auth initialization and Supabase auth listener
  useEffect(() => {
    let mounted = true;

    // Initialize auth on mount
    initializeAuth();

    // Listen for Supabase auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.debug("[Auth] Supabase auth event:", event, {
          hasSession: !!session,
        });

        if (!mounted) return;

        switch (event) {
          case "SIGNED_IN":
          case "TOKEN_REFRESHED":
            if (session) {
              // Skip if MFA verification is pending - we'll set session after MFA completes
              if (mfaPendingRef.current) {
                logger.debug("[Auth] Skipping session activation - MFA verification pending",
                );
                return;
              }
              logger.debug("[Auth] User signed in or token refreshed");
              setSession(session);
              setUser(session.user);
              setError(null);
              // Initialize logger with user email for admin logging
              initLogger(session.user.email || null);
              void persistSessionToBackend(session);
            }
            break;

          case "SIGNED_OUT":
            logger.debug("[Auth] User signed out");
            setSession(null);
            setUser(null);
            setError(null);
            // Clear logger (non-admin mode)
            initLogger(null);
            await clearBackendSession();
            break;

          case "USER_UPDATED":
            if (session) {
              logger.debug("[Auth] User updated");
              setSession(session);
              setUser(session.user);
              // Re-initialize logger in case email changed
              initLogger(session.user.email || null);
              void persistSessionToBackend(session);
            }
            break;
        }
      },
    );

    return () => {
      mounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [initializeAuth]);

  // NOTE: Removed automatic redirect to login from AuthProvider
  // The ProtectedRoute component now handles showing beta modal before redirecting
  // This allows unauthenticated users to see the beta code modal first

  const signIn = async (
    email: string,
    password: string,
    rememberMe: boolean = false,
  ): Promise<SignInResult> => {
    try {
      setError(null);

      logger.debug("[Auth] Signing in with Supabase...");

      // Block onAuthStateChange from setting session - we'll handle it after MFA check
      mfaPendingRef.current = true;

      // Use Supabase authentication
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          // Set session persistence based on rememberMe
          // Supabase will handle the token expiration automatically
        },
      });

      if (error) {
        logger.error("[Auth] Sign in error:", error);
        throw error;
      }

      if (data.session && data.user) {
        logger.debug("[Auth] Sign in successful");

        // Check MFA status BEFORE exposing session to app
        const { data: mfaData, error: mfaError } =
          await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

        if (!mfaError && mfaData) {
          const requiresMFA =
            mfaData.currentLevel === "aal1" && mfaData.nextLevel === "aal2";

          if (requiresMFA) {
            logger.debug("[Auth] MFA verification required - deferring session activation",
            );
            // Set ref FIRST to block onAuthStateChange from setting session
            mfaPendingRef.current = true;
            // Store session/user in pending state (NOT exposed to app)
            setPendingSession(data.session);
            setPendingUser(data.user);
            setPendingRememberMe(rememberMe);
            const targetPath = location.state?.from || "/dashboard";
            setPendingNavigationTarget(targetPath);
            setPendingMFAVerification(true);
            // Don't set user/session or navigate - wait for MFA verification
            return { requiresMFA: true };
          }
        }

        // No MFA required - proceed with normal login flow
        // Clear the blocking flag since we're handling session activation ourselves
        mfaPendingRef.current = false;
        setSession(data.session);
        setUser(data.user);

        // Store rememberMe preference for session management
        if (rememberMe) {
          localStorage.setItem("vitly_remember_me", "true");
        } else {
          localStorage.removeItem("vitly_remember_me");
        }

        // Set user context for monitoring (Sentry + Analytics)
        setUserContext({
          id: data.user.id,
          email: data.user.email || undefined,
        });

        analytics.identify(data.user.id, {
          email: data.user.email,
          login_method: "email",
        });

        // CRITICAL: Persist session to backend cookies BEFORE making any API calls
        // This ensures httpOnly cookies are set before checkBetaAccess or dashboard APIs
        await persistSessionToBackend(data.session);

        // Check beta access after successful sign-in
        await checkBetaAccess();

        // Navigate to intended destination or dashboard
        const from = location.state?.from || "/dashboard";
        navigate(from, { replace: true });
      }

      return {};
    } catch (err: unknown) {
      logger.error("[Auth] Sign in error:", err);
      // Reset the MFA pending flag on error
      mfaPendingRef.current = false;
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign in";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    metadata?: SignUpMetadata,
  ): Promise<SignUpResult> => {
    try {
      setError(null);

      logger.debug("[Auth] Signing up via backend API...");
      logger.debug("[Auth] Metadata being sent:", metadata);

      // Call our backend which will validate beta code and create Supabase account
      const csrfToken = await getCsrfToken();

      const response = await fetch(`${API_URL}/v1/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({
          email,
          password,
          firstName: metadata?.firstName,
          lastName: metadata?.lastName,
          betaCode: metadata?.betaCode,
          guestToken: metadata?.guestToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        logger.error("[Auth] Sign up error:", data);

        // Parse validation errors if they exist
        if (data.message && data.message.startsWith("[")) {
          try {
            const validationErrors = JSON.parse(data.message);
            const errorMessages = validationErrors.map(
              (err: { path?: string[]; message?: string }) => {
                if (err.path && err.message) {
                  return `${err.path[0]}: ${err.message}`;
                }
                return err.message || "Validation error";
              },
            );
            throw new Error(errorMessages.join(", "));
          } catch (_parseError) {
            throw new Error(
              data.error || data.message || "Registration failed",
            );
          }
        }

        throw new Error(data.error || data.message || "Registration failed");
      }

      logger.debug("[Auth] Sign up successful:", data);

      // Check if email confirmation is required
      if (data.requiresEmailConfirmation) {
        logger.debug("[Auth] Email confirmation required");
        return {
          requiresEmailConfirmation: true,
          user: data.user,
        };
      }

      // If we have a session, log the user in
      if (data.session && data.user) {
        logger.debug("[Auth] Auto-signed in after registration");

        // Set the session in Supabase client
        await supabase.auth.setSession({
          access_token: data.accessToken || data.session.access_token,
          refresh_token: data.refreshToken || data.session.refresh_token,
        });

        setSession(data.session);
        setUser(data.user);

        // Set user context for monitoring (Sentry + Analytics)
        setUserContext({
          id: data.user.id,
          email: data.user.email || undefined,
        });

        analytics.identify(data.user.id, {
          email: data.user.email,
          signup_method: "email",
          beta_code: metadata?.betaCode || "BETA2025",
        });

        // Track beta signup conversion
        analytics.trackBetaSignup("email", metadata?.betaCode);

        // Check beta access after successful registration
        await checkBetaAccess();

        navigate("/dashboard", { replace: true });
      }

      return data;
    } catch (err: unknown) {
      logger.error("[Auth] Sign up error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign up";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signOut = async () => {
    try {
      setError(null);

      logger.debug("[Auth] Signing out from Supabase...");

      // Clear monitoring context before logout
      clearUserContext();
      clearUser();
      analytics.reset();

      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();

      if (error) {
        logger.error("[Auth] Sign out error:", error);
        throw error;
      }

      // Clear any local storage items
      localStorage.removeItem("vitly_remember_me");
      sessionStorage.clear();

      // Clear React Query cache to prevent stale data from previous user
      queryClient.clear();

      // Reset state
      setSession(null);
      setUser(null);
      setError(null);
      setBetaStatus(null);

      // Navigate to login
      navigate("/login", { replace: true });
    } catch (err: unknown) {
      logger.error("[Auth] Sign out error:", err);
      setError(err instanceof Error ? err.message : "Failed to sign out");

      // Force local sign out even if request fails
      queryClient.clear();
      setSession(null);
      setUser(null);
      navigate("/login", { replace: true });
    }
  };

  const refreshSession = async () => {
    try {
      logger.debug("[Auth] Refreshing Supabase session...");

      // Refresh the session using Supabase
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        logger.error("[Auth] Refresh session error:", error);
        throw error;
      }

      if (data.session) {
        logger.debug("[Auth] Session refreshed successfully");
        setSession(data.session);
        setUser(data.session.user);
      }
    } catch (err: unknown) {
      logger.error("[Auth] Refresh session error:", err);
      setError(
        err instanceof Error ? err.message : "Failed to refresh session",
      );
      throw err;
    }
  };

  const checkBetaAccess = async () => {
    try {
      logger.debug("[Auth] Checking beta access...");

      const { data } = await apiClient.api.get("/v1/beta/check-access");
      logger.debug("[Auth] Beta access check successful:", data);
      setBetaStatus(data);
    } catch (err: unknown) {
      const axiosError = err as { response?: { status: number } };
      const status = axiosError?.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        logger.debug("[Auth] User does not have beta access");
        setBetaStatus({ hasAccess: false });
        return;
      }

      logger.error("[Auth] Beta access check error:", err);
      setBetaStatus({ hasAccess: false });
    }
  };

  // MFA verification handlers
  const handleMFASuccess = async () => {
    logger.debug("[Auth] MFA verification successful");

    mfaPendingRef.current = false;

    if (pendingSession && pendingUser) {
      setSession(pendingSession);
      setUser(pendingUser);

      // Store rememberMe preference
      if (pendingRememberMe) {
        localStorage.setItem("vitly_remember_me", "true");
      } else {
        localStorage.removeItem("vitly_remember_me");
      }

      // Set user context for monitoring (Sentry + Analytics)
      setUserContext({
        id: pendingUser.id,
        email: pendingUser.email || undefined,
      });

      analytics.identify(pendingUser.id, {
        email: pendingUser.email,
        login_method: "email",
      });

      // Initialize logger with user email
      initLogger(pendingUser.email || null);

      // CRITICAL: Persist session to backend cookies BEFORE making any API calls
      await persistSessionToBackend(pendingSession);
    }

    // Clear pending state
    setPendingMFAVerification(false);
    setPendingSession(null);
    setPendingUser(null);
    setPendingRememberMe(false);

    // Complete the login flow
    await checkBetaAccess();

    // Navigate to the originally intended destination
    const targetPath = pendingNavigationTarget || "/dashboard";
    setPendingNavigationTarget(null);
    navigate(targetPath, { replace: true });
  };

  const handleMFACancel = async () => {
    logger.debug("[Auth] MFA verification cancelled");

    // Clear the MFA pending flag
    mfaPendingRef.current = false;

    // Clear pending state
    setPendingMFAVerification(false);
    setPendingNavigationTarget(null);
    setPendingSession(null);
    setPendingUser(null);
    setPendingRememberMe(false);

    // Sign out from Supabase since they cancelled MFA
    // (we have an active Supabase session even though we didn't expose it to the app)
    try {
      await supabase.auth.signOut();
    } catch (err) {
      logger.error("[Auth] Error signing out after MFA cancel:", err);
    }

    // Navigate to login
    navigate("/login", { replace: true });
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    error,
    betaStatus,
    authType: 'supabase',
    signIn,
    signUp,
    signOut,
    /** @deprecated Use signOut */
    logout: signOut,
    refreshSession,
    checkBetaAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {pendingMFAVerification && (
          <MFAChallenge
            onSuccess={handleMFASuccess}
            onCancel={handleMFACancel}
          />
        )}
      </AnimatePresence>
    </AuthContext.Provider>
  );
};

export default AuthContext;
